import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Card } from 'react-bootstrap';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { formatSourceCount } from '../../utils/source-insights';
import { getHelpHtml } from '../../utils/help/help-content';
import type { DateRangeQueryOptions } from '../../utils/visibility-range';
import type { CompetitorSourceGapResult } from '../../types';
import DataLoadError from '@/components/errors/DataLoadError';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { competitorSourceGapColumnConfig } from '../../config/competitorSourceGapColumnConfig';

type AgGridWrapperProps = {
  table_name?: string;
  height?: string;
  columns: unknown[];
  data: unknown[];
  total?: number;
  settings?: Record<string, unknown>;
};
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

export function CompetitorSourceGapTable({
  projectId,
  rangeQuery,
}: {
  projectId: string;
  rangeQuery: DateRangeQueryOptions;
}) {
  const [data, setData] = useState<CompetitorSourceGapResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const queryKey = useMemo(() => JSON.stringify(rangeQuery), [rangeQuery]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api
      .getCompetitorSourceGap(projectId, rangeQuery)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, queryKey, rangeQuery, retryNonce]);

  const columns = useMemo(
    () =>
      competitorSourceGapColumnConfig({
        expandedId,
        onToggle: (id) => setExpandedId((current) => (current === id ? null : id)),
      }),
    [expandedId],
  );

  const expandedRow = data?.gaps.find((row) => row.sourceDomainId === expandedId) ?? null;

  if (loading && !error) {
    return <AgGridSkeleton height="360px" />;
  }

  return (
    <Card className="overflow-hidden ag-grid-in-card">
      <FalconCardHeader
        light
        titleTag="h6"
        className="py-3"
        title={
          <div>
            <span className="d-inline-flex align-items-center gap-1">
              شکاف منابع رقبا
              <HelpTooltip html={getHelpHtml('competitor_source_gap')} />
            </span>
            <p className="fs--2 text-700 mb-0 fw-normal mt-2">
              منابعی که رقبا در پاسخ‌های AI دارند اما برند شما کمتر یا اصلاً ندارد
            </p>
          </div>
        }
      />

      {error ? (
        <div className="px-3 py-4">
          <DataLoadError
            compact
            onRetry={() => setRetryNonce(n => n + 1)}
            isRetrying={loading}
          />
        </div>
      ) : !data || data.gaps.length === 0 ? (
        <p className="py-5 text-center fs--1 text-500 mb-0">
          در این بازه شکاف منبعی بین شما و رقبا شناسایی نشد.
        </p>
      ) : (
        <Card.Body>
          <TypedAgGridWrapper
            table_name="tracker_ai_competitor_source_gap"
            height="auto"
            columns={columns}
            data={data.gaps}
            total={data.gaps.length}
            settings={{ pagination: true, fullscreen: false, sideBar: false, export: { enabled: false } }}
          />

          {expandedRow && (
            <div className="border-top mt-3 pt-3">
              <p className="fs--2 fw-semibold text-uppercase text-500 mb-2">
                رقبای استفاده‌کننده از این منبع
              </p>
              <div className="d-flex flex-column gap-2">
                {expandedRow.competitorsUsing.map((competitor) => (
                  <div
                    key={competitor.competitorId}
                    className="d-flex flex-wrap align-items-center justify-content-between gap-2 rounded-3 border bg-body-tertiary px-3 py-2"
                  >
                    <span className="fw-medium text-1000">{competitor.brandName}</span>
                    <span className="fs--2 text-500">
                      {formatSourceCount(competitor.citations)} citation ·{' '}
                      {formatSourceCount(competitor.aiResponses)} پاسخ AI
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card.Body>
      )}
    </Card>
  );
}
